<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import {
  NCard,
  NForm,
  NFormItem,
  NInput,
  NSelect,
  NButton,
  NAlert,
  NSpace,
  NIcon,
  NModal,
  NSteps,
  NStep,
  NDivider,
  NSpin,
  NInputNumber,
  NDatePicker,
  NCheckbox,
  NSwitch,
  NGrid,
  NGridItem,
  NDynamicInput
} from 'naive-ui';
import {
  User,
  Lock,
  Shield,
  Save,
  Eye,
  EyeOff,
  Key,
  ArrowLeft,
  ArrowRight
} from 'lucide-vue-next';

import { authToken } from '@/services/auth';
import {
  createClientProfile,
  updateClientProfile,
  fetchMyClientProfile
} from '@/services/client';
import { encryptClientData, decryptClientData, hashSecurityAnswer, hashSecurityQuestion, derivePasswordFromSecurity } from '@/services/encryption.js';

const router = useRouter();

// Component state
const isLoading = ref(false);
const isSaving = ref(false);
const errorMessage = ref('');
const successMessage = ref('');
const isEditMode = ref(false);
const currentStep = ref(1);
const showPassword = ref(false);
const showConfirmPassword = ref(false);

// Password and security setup (Step 1)
const passwordSetup = ref({
  password: '',
  confirmPassword: '',
  securityQuestion1: '',
  securityAnswer1: '',
  securityQuestion2: '',
  securityAnswer2: '',
  securityQuestion3: '',
  securityAnswer3: ''
});

// Complete legal client profile data (Step 2) - ALL FIELDS
const clientData = ref({
  dateOfBirth: null,
  gender: "",
  fullAddress: "",
  phoneNumber: "",
  email: "",
  emergencyContactName: "",
  emergencyContactPhone: "",
  emergencyContactRelationship: "",
  legalMatters: [],
  previousLegalIssues: [],
  currentLegalCases: [],
  preferredLanguage: "",
  preferredCommunicationMethod: "",
  clientType: "",
  incomeLevel: "",
  employmentStatus: "",
  businessType: "",
  businessIndustry: "",
  businessSize: "",
  businessRevenue: null,
  legalBudget: null,
  urgencyLevel: "",
  referralSource: "",
  previousAttorneys: [],
  legalDocuments: [],
  courtCases: [],
  personalInfo: {
    maritalStatus: "",
    dependents: null,
    citizenship: "",
    educationLevel: "",
    occupation: "",
    employer: ""
  },
  financialInfo: {
    annualIncome: null,
    assets: [],
    liabilities: [],
    bankruptcyHistory: false,
    creditScore: ""
  },
  accessibilityNeeds: [],
  specialRequirements: "",
  conflictOfInterest: false,
  backgroundCheck: false,
  consentSigned: false,
  notes: ""
});

// For edit mode - existing profile decryption
const existingPassword = ref('');
const showExistingPassword = ref(false);
const showPasswordModal = ref(false);
const isDecrypting = ref(false);
const originalEncryptedData = ref(null);

// Security question options
const securityQuestions = [
  { label: "What was the name of your first pet?", value: "What was the name of your first pet?" },
  { label: "What city were you born in?", value: "What city were you born in?" },
  { label: "What was your mother's maiden name?", value: "What was your mother's maiden name?" },
  { label: "What was the name of your first school?", value: "What was the name of your first school?" },
  { label: "What was your childhood nickname?", value: "What was your childhood nickname?" },
  { label: "What is your favorite movie?", value: "What is your favorite movie?" },
  { label: "What street did you grow up on?", value: "What street did you grow up on?" },
  { label: "What was the name of your favorite teacher?", value: "What was the name of your favorite teacher?" },
  { label: "What is your favorite food?", value: "What is your favorite food?" },
  { label: "What was the name of your best friend in school?", value: "What was the name of your best friend in school?" },
  { label: "What is your maternal grandmother's first name?", value: "What is your maternal grandmother's first name?" },
  { label: "What was your favorite subject in school?", value: "What was your favorite subject in school?" },
  { label: "What is the name of your hometown?", value: "What is the name of your hometown?" },
  { label: "What was your father's middle name?", value: "What was your father's middle name?" },
  { label: "What was your favorite childhood game?", value: "What was your favorite childhood game?" }
];

// Legal client specific options
const genderOptions = [
  { label: 'Male', value: 'male' },
  { label: 'Female', value: 'female' },
  { label: 'Non-binary', value: 'non-binary' },
  { label: 'Other', value: 'other' },
  { label: 'Prefer not to say', value: 'prefer-not-to-say' }
];

const languageOptions = [
  { label: 'English', value: 'english' },
  { label: 'Hindi', value: 'hindi' },
  { label: 'Bengali', value: 'bengali' },
  { label: 'Telugu', value: 'telugu' },
  { label: 'Marathi', value: 'marathi' },
  { label: 'Tamil', value: 'tamil' },
  { label: 'Urdu', value: 'urdu' },
  { label: 'Gujarati', value: 'gujarati' },
  { label: 'Kannada', value: 'kannada' },
  { label: 'Odia', value: 'odia' },
  { label: 'Punjabi', value: 'punjabi' },
  { label: 'Malayalam', value: 'malayalam' },
  { label: 'Assamese', value: 'assamese' },
  { label: 'Maithili', value: 'maithili' },
  { label: 'Santali', value: 'santali' },
  { label: 'Konkani', value: 'konkani' },
  { label: 'Sindhi', value: 'sindhi' },
  { label: 'Dogri', value: 'dogri' },
  { label: 'Manipuri', value: 'manipuri' },
  { label: 'Bodo', value: 'bodo' },
  { label: 'Kashmiri', value: 'kashmiri' },
  { label: 'Other', value: 'other' }
];

const communicationMethodOptions = [
  { label: 'Phone', value: 'phone' },
  { label: 'Email', value: 'email' },
  { label: 'Text Message', value: 'text' },
  { label: 'Video Call', value: 'video' },
  { label: 'In-Person Only', value: 'in-person' },
  { label: 'Through Representative', value: 'representative' },
  { label: 'Written Mail', value: 'mail' }
];

const clientTypeOptions = [
  { label: 'Individual', value: 'individual' },
  { label: 'Small Business', value: 'small_business' },
  { label: 'Corporation', value: 'corporation' },
  { label: 'Non-Profit Organization', value: 'non_profit' },
  { label: 'Government Entity', value: 'government' },
  { label: 'Partnership', value: 'partnership' },
  { label: 'Trust/Estate', value: 'trust_estate' }
];

const incomeLevelOptions = [
  { label: 'Below $25,000', value: 'low' },
  { label: '$25,000 - $50,000', value: 'lower_middle' },
  { label: '$50,000 - $100,000', value: 'middle' },
  { label: '$100,000 - $200,000', value: 'upper_middle' },
  { label: 'Above $200,000', value: 'high' },
  { label: 'Prefer not to disclose', value: 'undisclosed' }
];

const employmentStatusOptions = [
  { label: 'Employed Full-time', value: 'full_time' },
  { label: 'Employed Part-time', value: 'part_time' },
  { label: 'Self-employed', value: 'self_employed' },
  { label: 'Business Owner', value: 'business_owner' },
  { label: 'Unemployed', value: 'unemployed' },
  { label: 'Retired', value: 'retired' },
  { label: 'Student', value: 'student' },
  { label: 'Disabled', value: 'disabled' }
];

const businessTypeOptions = [
  { label: 'Sole Proprietorship', value: 'sole_proprietorship' },
  { label: 'Partnership', value: 'partnership' },
  { label: 'Limited Liability Company (LLC)', value: 'llc' },
  { label: 'Corporation (C-Corp)', value: 'c_corp' },
  { label: 'S-Corporation', value: 's_corp' },
  { label: 'Non-Profit', value: 'non_profit' },
  { label: 'Professional Corporation', value: 'professional_corp' }
];

const businessIndustryOptions = [
  { label: 'Technology', value: 'technology' },
  { label: 'Healthcare', value: 'healthcare' },
  { label: 'Finance', value: 'finance' },
  { label: 'Real Estate', value: 'real_estate' },
  { label: 'Manufacturing', value: 'manufacturing' },
  { label: 'Retail', value: 'retail' },
  { label: 'Hospitality', value: 'hospitality' },
  { label: 'Construction', value: 'construction' },
  { label: 'Education', value: 'education' },
  { label: 'Legal Services', value: 'legal' },
  { label: 'Consulting', value: 'consulting' },
  { label: 'Other', value: 'other' }
];

const businessSizeOptions = [
  { label: '1-5 employees', value: 'micro' },
  { label: '6-25 employees', value: 'small' },
  { label: '26-100 employees', value: 'medium' },
  { label: '101-500 employees', value: 'large' },
  { label: '500+ employees', value: 'enterprise' }
];

const urgencyLevelOptions = [
  { label: 'Immediate (within days)', value: 'immediate' },
  { label: 'Urgent (within weeks)', value: 'urgent' },
  { label: 'Moderate (within months)', value: 'moderate' },
  { label: 'Low (no rush)', value: 'low' }
];

const referralSourceOptions = [
  { label: 'Internet Search', value: 'internet' },
  { label: 'Friend/Family', value: 'referral' },
  { label: 'Another Attorney', value: 'attorney_referral' },
  { label: 'Bar Association', value: 'bar_association' },
  { label: 'Advertisement', value: 'advertisement' },
  { label: 'Social Media', value: 'social_media' },
  { label: 'Previous Client', value: 'repeat_client' }
];

const maritalStatusOptions = [
  { label: 'Single', value: 'single' },
  { label: 'Married', value: 'married' },
  { label: 'Divorced', value: 'divorced' },
  { label: 'Widowed', value: 'widowed' },
  { label: 'Separated', value: 'separated' },
  { label: 'Domestic Partnership', value: 'domestic_partnership' },
  { label: 'Prefer not to say', value: 'prefer_not_to_say' }
];

const citizenshipOptions = [
  { label: 'US Citizen', value: 'us_citizen' },
  { label: 'Permanent Resident', value: 'permanent_resident' },
  { label: 'Temporary Visa', value: 'temporary_visa' },
  { label: 'Work Visa', value: 'work_visa' },
  { label: 'Student Visa', value: 'student_visa' },
  { label: 'Other', value: 'other' }
];

const educationLevelOptions = [
  { label: 'High School', value: 'high_school' },
  { label: 'Associate Degree', value: 'associate' },
  { label: 'Bachelor\'s Degree', value: 'bachelor' },
  { label: 'Master\'s Degree', value: 'master' },
  { label: 'Doctoral Degree', value: 'doctoral' },
  { label: 'Professional Degree', value: 'professional' },
  { label: 'Other', value: 'other' }
];

const creditScoreOptions = [
  { label: 'Excellent (750+)', value: 'excellent' },
  { label: 'Good (700-749)', value: 'good' },
  { label: 'Fair (650-699)', value: 'fair' },
  { label: 'Poor (600-649)', value: 'poor' },
  { label: 'Very Poor (below 600)', value: 'very_poor' },
  { label: 'Unknown', value: 'unknown' }
];

const accessibilityNeedsOptions = [
  { label: 'Wheelchair Access', value: 'wheelchair' },
  { label: 'Hearing Assistance', value: 'hearing' },
  { label: 'Visual Assistance', value: 'visual' },
  { label: 'ASL Interpreter', value: 'asl' },
  { label: 'Language Interpreter', value: 'interpreter' },
  { label: 'Sensory Sensitivities', value: 'sensory' },
  { label: 'Mobility Assistance', value: 'mobility' },
  { label: 'Cognitive/Learning Support', value: 'cognitive' },
  { label: 'Service Animal Accommodation', value: 'service_animal' },
  { label: 'No specific needs', value: 'none' }
];

// Dynamic input creator functions
const createLegalMatterItem = () => "";
const createPreviousLegalIssueItem = () => "";
const createCurrentLegalCaseItem = () => ({ caseType: "", status: "", attorney: "" });
const createPreviousAttorneyItem = () => ({ name: "", firm: "", practiceArea: "" });
const createLegalDocumentItem = () => ({ type: "", description: "", date: null });
const createCourtCaseItem = () => ({ caseNumber: "", court: "", status: "", date: null });
const createAssetItem = () => ({ type: "", value: null, description: "" });
const createLiabilityItem = () => ({ type: "", amount: null, description: "" });

// Computed properties
const isPasswordValid = computed(() => {
  return passwordSetup.value.password.length >= 8 &&
    passwordSetup.value.password === passwordSetup.value.confirmPassword;
});

const areSecurityQuestionsValid = computed(() => {
  const setup = passwordSetup.value;
  return setup.securityQuestion1 && setup.securityAnswer1.trim() &&
    setup.securityQuestion2 && setup.securityAnswer2.trim() &&
    setup.securityQuestion3 && setup.securityAnswer3.trim() &&
    setup.securityQuestion1 !== setup.securityQuestion2 &&
    setup.securityQuestion1 !== setup.securityQuestion3 &&
    setup.securityQuestion2 !== setup.securityQuestion3;
});

const canProceedToStep2 = computed(() => {
  return isPasswordValid.value && areSecurityQuestionsValid.value;
});

const isProfileDataValid = computed(() => {
  return clientData.value.dateOfBirth &&
    clientData.value.gender &&
    clientData.value.consentSigned;
});

// Format date helper function
const formatDateForApi = (timestamp) => {
  if (!timestamp) return null;

  let date;
  if (typeof timestamp === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(timestamp)) {
      return timestamp;
    }
    date = new Date(timestamp);
  } else if (typeof timestamp === 'number') {
    date = new Date(timestamp);
  } else {
    return null;
  }

  if (isNaN(date.getTime())) {
    return null;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

// Parse date from API format
const parseDateFromApi = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date.getTime();
};

// Check for existing client profile
const checkExistingProfile = async () => {
  try {
    isLoading.value = true;
    const profile = await fetchMyClientProfile(authToken.value);

    if (profile) {
      isEditMode.value = true;
      originalEncryptedData.value = profile;
      showPasswordModal.value = true;
    }
  } catch (err) {
    console.log('No existing profile found:', err.message);
  } finally {
    isLoading.value = false;
  }
};

// Decrypt existing profile and populate form
const decryptExistingProfile = async () => {
  if (!existingPassword.value.trim()) {
    errorMessage.value = "Please enter your password";
    return;
  }

  try {
    isDecrypting.value = true;
    errorMessage.value = '';

    // Decrypt the client data using the password
    const decryptedData = await decryptClientData(
      originalEncryptedData.value.encrypted_client_data,
      originalEncryptedData.value.encryption_metadata,
      existingPassword.value
    );

    // Populate all the form fields with decrypted data
    clientData.value.dateOfBirth = parseDateFromApi(decryptedData.dateOfBirth);
    clientData.value.gender = decryptedData.gender || "";
    clientData.value.fullAddress = decryptedData.fullAddress || "";
    clientData.value.phoneNumber = decryptedData.phoneNumber || "";
    clientData.value.email = decryptedData.email || "";
    clientData.value.emergencyContactName = decryptedData.emergencyContactName || "";
    clientData.value.emergencyContactPhone = decryptedData.emergencyContactPhone || "";
    clientData.value.emergencyContactRelationship = decryptedData.emergencyContactRelationship || "";
    clientData.value.legalMatters = Array.isArray(decryptedData.legalMatters) ? decryptedData.legalMatters : [];
    clientData.value.previousLegalIssues = Array.isArray(decryptedData.previousLegalIssues) ? decryptedData.previousLegalIssues : [];
    
    clientData.value.currentLegalCases = Array.isArray(decryptedData.currentLegalCases) ?
      decryptedData.currentLegalCases.map(item => ({
        caseType: item?.caseType || "",
        status: item?.status || "",
        attorney: item?.attorney || ""
      })) : [];

    clientData.value.preferredLanguage = decryptedData.preferredLanguage || "";
    clientData.value.preferredCommunicationMethod = decryptedData.preferredCommunicationMethod || "";
    clientData.value.clientType = decryptedData.clientType || "";
    clientData.value.incomeLevel = decryptedData.incomeLevel || "";
    clientData.value.employmentStatus = decryptedData.employmentStatus || "";
    clientData.value.businessType = decryptedData.businessType || "";
    clientData.value.businessIndustry = decryptedData.businessIndustry || "";
    clientData.value.businessSize = decryptedData.businessSize || "";
    clientData.value.businessRevenue = decryptedData.businessRevenue || null;
    clientData.value.legalBudget = decryptedData.legalBudget || null;
    clientData.value.urgencyLevel = decryptedData.urgencyLevel || "";
    clientData.value.referralSource = decryptedData.referralSource || "";

    clientData.value.previousAttorneys = Array.isArray(decryptedData.previousAttorneys) ?
      decryptedData.previousAttorneys.map(item => ({
        name: item?.name || "",
        firm: item?.firm || "",
        practiceArea: item?.practiceArea || ""
      })) : [];

    clientData.value.legalDocuments = Array.isArray(decryptedData.legalDocuments) ?
      decryptedData.legalDocuments.map(item => ({
        type: item?.type || "",
        description: item?.description || "",
        date: parseDateFromApi(item?.date)
      })) : [];

    clientData.value.courtCases = Array.isArray(decryptedData.courtCases) ?
      decryptedData.courtCases.map(item => ({
        caseNumber: item?.caseNumber || "",
        court: item?.court || "",
        status: item?.status || "",
        date: parseDateFromApi(item?.date)
      })) : [];

    // Handle personal info
    if (decryptedData.personalInfo && typeof decryptedData.personalInfo === 'object') {
      clientData.value.personalInfo = {
        maritalStatus: decryptedData.personalInfo.maritalStatus || "",
        dependents: decryptedData.personalInfo.dependents || null,
        citizenship: decryptedData.personalInfo.citizenship || "",
        educationLevel: decryptedData.personalInfo.educationLevel || "",
        occupation: decryptedData.personalInfo.occupation || "",
        employer: decryptedData.personalInfo.employer || ""
      };
    }

    // Handle financial info
    if (decryptedData.financialInfo && typeof decryptedData.financialInfo === 'object') {
      clientData.value.financialInfo = {
        annualIncome: decryptedData.financialInfo.annualIncome || null,
        assets: Array.isArray(decryptedData.financialInfo.assets) ?
          decryptedData.financialInfo.assets.map(item => ({
            type: item?.type || "",
            value: item?.value || null,
            description: item?.description || ""
          })) : [],
        liabilities: Array.isArray(decryptedData.financialInfo.liabilities) ?
          decryptedData.financialInfo.liabilities.map(item => ({
            type: item?.type || "",
            amount: item?.amount || null,
            description: item?.description || ""
          })) : [],
        bankruptcyHistory: Boolean(decryptedData.financialInfo.bankruptcyHistory),
        creditScore: decryptedData.financialInfo.creditScore || ""
      };
    }

    clientData.value.accessibilityNeeds = Array.isArray(decryptedData.accessibilityNeeds) ? decryptedData.accessibilityNeeds : [];
    clientData.value.specialRequirements = decryptedData.specialRequirements || "";
    clientData.value.conflictOfInterest = Boolean(decryptedData.conflictOfInterest);
    clientData.value.backgroundCheck = Boolean(decryptedData.backgroundCheck);
    clientData.value.consentSigned = Boolean(decryptedData.consentSigned);
    clientData.value.notes = decryptedData.notes || "";

    // Set password for re-encryption
    passwordSetup.value.password = existingPassword.value;
    passwordSetup.value.confirmPassword = existingPassword.value;

    // Set security questions from profile if available
    passwordSetup.value.securityQuestion1 = originalEncryptedData.value.security_question_1 || "";
    passwordSetup.value.securityQuestion2 = originalEncryptedData.value.security_question_2 || "";
    passwordSetup.value.securityQuestion3 = originalEncryptedData.value.security_question_3 || "";

    showPasswordModal.value = false;
    currentStep.value = 2;

  } catch (err) {
    console.error('Decryption failed:', err);
    errorMessage.value = "Failed to decrypt profile. Please check your password and try again.";
  } finally {
    isDecrypting.value = false;
  }
};

// Navigation functions
const goToStep1 = () => {
  if (isEditMode.value) return;
  currentStep.value = 1;
};

const goToStep2 = () => {
  if (!canProceedToStep2.value) {
    errorMessage.value = "Please complete the password and security setup first";
    return;
  }
  errorMessage.value = '';
  currentStep.value = 2;
};

// Save profile
const saveProfile = async () => {
  if (!isProfileDataValid.value) {
    errorMessage.value = "Please fill in all required fields and sign consent";
    return;
  }

  if (!isPasswordValid.value) {
    errorMessage.value = "Password setup is invalid";
    return;
  }

  try {
    isSaving.value = true;
    errorMessage.value = '';
    successMessage.value = '';

    // Format form data for API
    const formDataCopy = { ...clientData.value };

    // Format dates
    if (clientData.value.dateOfBirth) {
      formDataCopy.dateOfBirth = formatDateForApi(clientData.value.dateOfBirth);
    }

    // Format date fields within arrays
    if (clientData.value.legalDocuments && clientData.value.legalDocuments.length > 0) {
      formDataCopy.legalDocuments = clientData.value.legalDocuments.map(item => {
        if (item.date) {
          return { ...item, date: formatDateForApi(item.date) };
        }
        return item;
      });
    }

    if (clientData.value.courtCases && clientData.value.courtCases.length > 0) {
      formDataCopy.courtCases = clientData.value.courtCases.map(item => {
        if (item.date) {
          return { ...item, date: formatDateForApi(item.date) };
        }
        return item;
      });
    }

    const { encryptedData, encryptionMetadata } = await encryptClientData(
      formDataCopy,
      passwordSetup.value.password
    );

    const profilePayload = {
      encrypted_client_data: encryptedData,
      encryption_metadata: encryptionMetadata,
      consent_signed: clientData.value.consentSigned
    };

    // Derive key from security answers
    const securityKey = await derivePasswordFromSecurity([
      passwordSetup.value.securityAnswer1,
      passwordSetup.value.securityAnswer2,
      passwordSetup.value.securityAnswer3
    ]);

    // Encrypt the original password with the security key
    const encryptedPasswordResult = await encryptClientData(
      passwordSetup.value.password,
      securityKey
    );

    // Add fields for new profiles
    if (!isEditMode.value) {
      profilePayload.encrypted_recovery_password = encryptedPasswordResult.encryptedData; 
      profilePayload.recovery_encryption_metadata = encryptedPasswordResult.encryptionMetadata;
      profilePayload.security_question_1 = await hashSecurityQuestion(passwordSetup.value.securityQuestion1);
      profilePayload.security_answer_1_hash = await hashSecurityAnswer(passwordSetup.value.securityAnswer1);
      profilePayload.security_question_2 = await hashSecurityQuestion(passwordSetup.value.securityQuestion2);
      profilePayload.security_answer_2_hash = await hashSecurityAnswer(passwordSetup.value.securityAnswer2);
      profilePayload.security_question_3 = await hashSecurityQuestion(passwordSetup.value.securityQuestion3);
      profilePayload.security_answer_3_hash = await hashSecurityAnswer(passwordSetup.value.securityAnswer3);
    }

    console.log('Payload being sent:', JSON.stringify(profilePayload, null, 2));

    if (isEditMode.value) {
      await updateClientProfile(authToken.value, profilePayload);
      successMessage.value = "Client profile updated successfully!";
    } else {
      await createClientProfile(authToken.value, profilePayload);
      successMessage.value = "Client profile created successfully!";
    }

    setTimeout(() => {
      router.push('/client-profile');
    }, 2000);

  } catch (err) {
    console.error('Failed to save profile:', err);
    errorMessage.value = err.message || `Failed to ${isEditMode.value ? 'update' : 'create'} client profile. Please try again.`;
  } finally {
    isSaving.value = false;
  }
};

// Cancel editing
const cancelEdit = () => {
  router.push('/client-profile');
};

// Initialize component
onMounted(() => {
  checkExistingProfile();
});
</script>

<template>
  <div class="profile-edit-container">
    <!-- Loading State -->
    <div v-if="isLoading" class="loading-state">
      <NSpin size="large" />
      <p>Loading client profile...</p>
    </div>

    <!-- Password Modal for Edit Mode -->
    <NModal v-model:show="showPasswordModal" preset="card" title="Enter Your Password" :closable="false"
      :mask-closable="false" style="width: 500px">
      <div class="password-modal-content">
        <div class="modal-icon">
          <NIcon size="48" color="#2080f0">
            <Lock />
          </NIcon>
        </div>

        <p>Enter your client password to decrypt and edit your profile:</p>

        <NForm>
          <NFormItem>
            <NInput v-model:value="existingPassword" :type="showExistingPassword ? 'text' : 'password'"
              placeholder="Enter your client password" :disabled="isDecrypting" @keyup.enter="decryptExistingProfile">
              <template #suffix>
                <NButton text @click="showExistingPassword = !showExistingPassword" :disabled="isDecrypting">
                  <NIcon>
                    <Eye v-if="!showExistingPassword" />
                    <EyeOff v-else />
                  </NIcon>
                </NButton>
              </template>
            </NInput>
          </NFormItem>
        </NForm>

        <div v-if="errorMessage" class="error-message">
          <NAlert type="error" :title="errorMessage" closable />
        </div>
      </div>

      <template #footer>
        <NSpace justify="end">
          <NButton @click="() => router.push('/client-profile')" :disabled="isDecrypting">
            Cancel
          </NButton>
          <NButton type="primary" @click="decryptExistingProfile" :loading="isDecrypting"
            :disabled="!existingPassword.trim()">
            <template #icon>
              <NIcon>
                <Key />
              </NIcon>
            </template>
            Decrypt & Edit
          </NButton>
        </NSpace>
      </template>
    </NModal>

    <!-- Main Content -->
    <div class="edit-content">
      <div class="page-header">
        <h1>
          {{ isEditMode ? 'Edit Client Profile' : 'Create Client Profile' }}
        </h1>
        <p class="page-subtitle">
          {{ isEditMode ? 'Update your encrypted legal information' : 'Set up your secure, encrypted legal client profile'
          }}
        </p>
      </div>

      <!-- Progress Steps -->
      <NSteps :current="currentStep" class="steps-container">
        <NStep title="Security Setup" description="Password & Recovery Questions" />
        <NStep title="Profile Information" description="Legal & Personal Data" />
      </NSteps>

      <!-- Step 1: Password and Security Setup -->
      <div v-if="currentStep === 1 && !isEditMode">
        <NCard title="Password & Security Setup" class="step-card">
          <template #header-extra>
            <NIcon>
              <Shield />
            </NIcon>
          </template>

          <NAlert type="info" title="Security Notice" class="security-notice">
            Your password encrypts all your legal data. Choose a strong password and remember your security questions
            - they're your only way to recover access if you forget your password.
          </NAlert>

          <NForm label-placement="top">
            <!-- Password Setup -->
            <div class="form-section">
              <h3>Create Your Client Password</h3>

              <NFormItem label="Password (minimum 8 characters)" required>
                <NInput v-model:value="passwordSetup.password" :type="showPassword ? 'text' : 'password'"
                  placeholder="Enter a strong password">
                  <template #suffix>
                    <NButton text @click="showPassword = !showPassword">
                      <NIcon>
                        <Eye v-if="!showPassword" />
                        <EyeOff v-else />
                      </NIcon>
                    </NButton>
                  </template>
                </NInput>
              </NFormItem>

              <NFormItem label="Confirm Password" required>
                <NInput v-model:value="passwordSetup.confirmPassword" :type="showConfirmPassword ? 'text' : 'password'"
                  placeholder="Confirm your password">
                  <template #suffix>
                    <NButton text @click="showConfirmPassword = !showConfirmPassword">
                      <NIcon>
                        <Eye v-if="!showConfirmPassword" />
                        <EyeOff v-else />
                      </NIcon>
                    </NButton>
                  </template>
                </NInput>
              </NFormItem>

              <div v-if="passwordSetup.password && passwordSetup.confirmPassword && !isPasswordValid"
                class="password-error">
                <NAlert type="error" title="Passwords don't match or are too short" />
              </div>
            </div>

            <NDivider />

            <!-- Security Questions -->
            <div class="form-section">
              <h3>Security Questions for Password Recovery</h3>
              <p class="section-description">
                These questions will help you recover your password if you forget it. Choose questions only you know the
                answer to.
              </p>

              <NFormItem label="Security Question 1" required>
                <NSelect v-model:value="passwordSetup.securityQuestion1" :options="securityQuestions"
                  placeholder="Choose a security question" />
              </NFormItem>

              <NFormItem label="Answer 1" required>
                <NInput v-model:value="passwordSetup.securityAnswer1" placeholder="Your answer (case sensitive)" />
              </NFormItem>

              <NFormItem label="Security Question 2" required>
                <NSelect v-model:value="passwordSetup.securityQuestion2"
                  :options="securityQuestions.filter(q => q.value !== passwordSetup.securityQuestion1)"
                  placeholder="Choose a different security question" />
              </NFormItem>

              <NFormItem label="Answer 2" required>
                <NInput v-model:value="passwordSetup.securityAnswer2" placeholder="Your answer (case sensitive)" />
              </NFormItem>

              <NFormItem label="Security Question 3" required>
                <NSelect v-model:value="passwordSetup.securityQuestion3" :options="securityQuestions.filter(q =>
                  q.value !== passwordSetup.securityQuestion1 &&
                  q.value !== passwordSetup.securityQuestion2
                )" placeholder="Choose a third security question" />
              </NFormItem>

              <NFormItem label="Answer 3" required>
                <NInput v-model:value="passwordSetup.securityAnswer3" placeholder="Your answer (case sensitive)" />
              </NFormItem>
            </div>
          </NForm>

          <template #footer>
            <NSpace justify="end">
              <NButton @click="cancelEdit">
                Cancel
              </NButton>
              <NButton type="primary" @click="goToStep2" :disabled="!canProceedToStep2">
                <template #icon>
                  <NIcon>
                    <ArrowRight />
                  </NIcon>
                </template>
                Continue to Profile
              </NButton>
            </NSpace>
          </template>
        </NCard>
      </div>

      <!-- Step 2: Complete Profile Information -->
      <div v-if="currentStep === 2">
        <NCard title="Client Profile Information" class="step-card">
          <template #header-extra>
            <NIcon>
              <User />
            </NIcon>
          </template>

          <NForm label-placement="top">
            <!-- Basic Personal Information -->
            <div class="form-section">
              <h3>Personal Information</h3>

              <NFormItem label="Date of Birth" required>
                <NDatePicker v-model:value="clientData.dateOfBirth" type="date" clearable value-format="yyyy-MM-dd"
                  :is-date-disabled="(timestamp) => timestamp > Date.now()" />
              </NFormItem>

              <NFormItem label="Gender" required>
                <NSelect v-model:value="clientData.gender" :options="genderOptions" placeholder="Select gender"
                  clearable />
              </NFormItem>

              <NFormItem label="Full Address">
                <NInput v-model:value="clientData.fullAddress" type="textarea" placeholder="Enter your full address"
                  :autosize="{ minRows: 2, maxRows: 4 }" />
              </NFormItem>

              <div class="form-row">
                <NFormItem label="Phone Number">
                  <NInput v-model:value="clientData.phoneNumber" placeholder="Enter your phone number" />
                </NFormItem>

                <NFormItem label="Email Address">
                  <NInput v-model:value="clientData.email" placeholder="Enter your email" />
                </NFormItem>
              </div>

              <div class="form-row">
                <NFormItem label="Preferred Language">
                  <NSelect v-model:value="clientData.preferredLanguage" :options="languageOptions"
                    placeholder="Select your preferred language" clearable />
                </NFormItem>

                <NFormItem label="Communication Method">
                  <NSelect v-model:value="clientData.preferredCommunicationMethod"
                    :options="communicationMethodOptions" placeholder="Select preferred communication method"
                    clearable />
                </NFormItem>
              </div>
            </div>

            <NDivider />

            <!-- Legal Information -->
            <div class="form-section">
              <h3>Legal Information</h3>

              <div class="form-row">
                <NFormItem label="Client Type">
                  <NSelect v-model:value="clientData.clientType" :options="clientTypeOptions"
                    placeholder="Select client type" clearable />
                </NFormItem>

                <NFormItem label="Urgency Level">
                  <NSelect v-model:value="clientData.urgencyLevel" :options="urgencyLevelOptions"
                    placeholder="Select urgency level" clearable />
                </NFormItem>
              </div>

              <NFormItem label="Legal Matters">
                <NDynamicInput v-model:value="clientData.legalMatters" :on-create="createLegalMatterItem"
                  placeholder="Add legal matter">
                  <template #default="{ value, index }">
                    <NInput v-model:value="clientData.legalMatters[index]" placeholder="Legal matter or issue" />
                  </template>
                </NDynamicInput>
              </NFormItem>

              <NFormItem label="Previous Legal Issues">
                <NDynamicInput v-model:value="clientData.previousLegalIssues" :on-create="createPreviousLegalIssueItem"
                  placeholder="Add previous legal issue">
                  <template #default="{ value, index }">
                    <NInput v-model:value="clientData.previousLegalIssues[index]" placeholder="Previous legal issue" />
                  </template>
                </NDynamicInput>
              </NFormItem>

              <NFormItem label="Current Legal Cases">
                <NDynamicInput v-model:value="clientData.currentLegalCases" :on-create="createCurrentLegalCaseItem"
                  placeholder="Add current legal case">
                  <template #default="{ value, index }">
                    <div class="dynamic-input-row">
                      <NInput v-model:value="clientData.currentLegalCases[index].caseType" placeholder="Case type" />
                      <NInput v-model:value="clientData.currentLegalCases[index].status" placeholder="Status" />
                      <NInput v-model:value="clientData.currentLegalCases[index].attorney" placeholder="Attorney/Firm" />
                    </div>
                  </template>
                </NDynamicInput>
              </NFormItem>

              <NFormItem label="Previous Attorneys">
                <NDynamicInput v-model:value="clientData.previousAttorneys" :on-create="createPreviousAttorneyItem"
                  placeholder="Add previous attorney">
                  <template #default="{ value, index }">
                    <div class="dynamic-input-row">
                      <NInput v-model:value="clientData.previousAttorneys[index].name" placeholder="Attorney name" />
                      <NInput v-model:value="clientData.previousAttorneys[index].firm" placeholder="Law firm" />
                      <NInput v-model:value="clientData.previousAttorneys[index].practiceArea" placeholder="Practice area" />
                    </div>
                  </template>
                </NDynamicInput>
              </NFormItem>

              <NFormItem label="Legal Documents">
                <NDynamicInput v-model:value="clientData.legalDocuments" :on-create="createLegalDocumentItem"
                  placeholder="Add legal document">
                  <template #default="{ value, index }">
                    <div class="dynamic-input-row">
                      <NInput v-model:value="clientData.legalDocuments[index].type" placeholder="Document type" />
                      <NInput v-model:value="clientData.legalDocuments[index].description" placeholder="Description" />
                      <NDatePicker v-model:value="clientData.legalDocuments[index].date" type="date" clearable
                        value-format="yyyy-MM-dd" placeholder="Date" />
                    </div>
                  </template>
                </NDynamicInput>
              </NFormItem>

              <NFormItem label="Court Cases">
                <NDynamicInput v-model:value="clientData.courtCases" :on-create="createCourtCaseItem"
                  placeholder="Add court case">
                  <template #default="{ value, index }">
                    <div class="dynamic-input-row">
                      <NInput v-model:value="clientData.courtCases[index].caseNumber" placeholder="Case number" />
                      <NInput v-model:value="clientData.courtCases[index].court" placeholder="Court" />
                      <NInput v-model:value="clientData.courtCases[index].status" placeholder="Status" />
                      <NDatePicker v-model:value="clientData.courtCases[index].date" type="date" clearable
                        value-format="yyyy-MM-dd" placeholder="Date" />
                    </div>
                  </template>
                </NDynamicInput>
              </NFormItem>

              <div class="form-row">
                <NFormItem label="Legal Budget">
                  <NInputNumber v-model:value="clientData.legalBudget" placeholder="Budget for legal services" clearable
                    :min="0" :formatter="(value) => value ? `$${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''"
                    :parser="(value) => value ? value.replace(/\$\s?|(,*)/g, '') : ''" />
                </NFormItem>

                <NFormItem label="Referral Source">
                  <NSelect v-model:value="clientData.referralSource" :options="referralSourceOptions"
                    placeholder="How did you find us?" clearable />
                </NFormItem>
              </div>
            </div>

            <NDivider />

            <!-- Business Information (if applicable) -->
            <div class="form-section">
              <h3>Business Information (if applicable)</h3>

              <div class="form-row">
                <NFormItem label="Business Type">
                  <NSelect v-model:value="clientData.businessType" :options="businessTypeOptions"
                    placeholder="Select business type" clearable />
                </NFormItem>

                <NFormItem label="Industry">
                  <NSelect v-model:value="clientData.businessIndustry" :options="businessIndustryOptions"
                    placeholder="Select industry" clearable />
                </NFormItem>
              </div>

              <div class="form-row">
                <NFormItem label="Business Size">
                  <NSelect v-model:value="clientData.businessSize" :options="businessSizeOptions"
                    placeholder="Select business size" clearable />
                </NFormItem>

                <NFormItem label="Annual Revenue">
                  <NInputNumber v-model:value="clientData.businessRevenue" placeholder="Annual business revenue" clearable
                    :min="0" :formatter="(value) => value ? `$${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''"
                    :parser="(value) => value ? value.replace(/\$\s?|(,*)/g, '') : ''" />
                </NFormItem>
              </div>
            </div>

            <NDivider />

            <!-- Personal Details -->
            <div class="form-section">
              <h3>Personal Details</h3>

              <div class="form-row">
                <NFormItem label="Employment Status">
                  <NSelect v-model:value="clientData.employmentStatus" :options="employmentStatusOptions"
                    placeholder="Select employment status" clearable />
                </NFormItem>

                <NFormItem label="Income Level">
                  <NSelect v-model:value="clientData.incomeLevel" :options="incomeLevelOptions"
                    placeholder="Select income level" clearable />
                </NFormItem>
              </div>

              <NGrid cols="2" x-gap="12" responsive="screen">
                <NGridItem>
                  <NFormItem label="Marital Status">
                    <NSelect v-model:value="clientData.personalInfo.maritalStatus" :options="maritalStatusOptions"
                      placeholder="Select marital status" clearable />
                  </NFormItem>
                </NGridItem>
                <NGridItem>
                  <NFormItem label="Number of Dependents">
                    <NInputNumber v-model:value="clientData.personalInfo.dependents" placeholder="Number of dependents"
                      :min="0" clearable />
                  </NFormItem>
                </NGridItem>
                <NGridItem>
                  <NFormItem label="Citizenship Status">
                    <NSelect v-model:value="clientData.personalInfo.citizenship" :options="citizenshipOptions"
                      placeholder="Select citizenship status" clearable />
                  </NFormItem>
                </NGridItem>
                <NGridItem>
                  <NFormItem label="Education Level">
                    <NSelect v-model:value="clientData.personalInfo.educationLevel" :options="educationLevelOptions"
                      placeholder="Select education level" clearable />
                  </NFormItem>
                </NGridItem>
                <NGridItem>
                  <NFormItem label="Occupation">
                    <NInput v-model:value="clientData.personalInfo.occupation" placeholder="Your occupation" />
                  </NFormItem>
                </NGridItem>
                <NGridItem>
                  <NFormItem label="Employer">
                    <NInput v-model:value="clientData.personalInfo.employer" placeholder="Current employer" />
                  </NFormItem>
                </NGridItem>
              </NGrid>
            </div>

            <NDivider />

            <!-- Financial Information -->
            <div class="form-section">
              <h3>Financial Information</h3>

              <div class="form-row">
                <NFormItem label="Annual Income">
                  <NInputNumber v-model:value="clientData.financialInfo.annualIncome" placeholder="Annual income" clearable
                    :min="0" :formatter="(value) => value ? `$${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''"
                    :parser="(value) => value ? value.replace(/\$\s?|(,*)/g, '') : ''" />
                </NFormItem>

                <NFormItem label="Credit Score">
                  <NSelect v-model:value="clientData.financialInfo.creditScore" :options="creditScoreOptions"
                    placeholder="Select credit score range" clearable />
                </NFormItem>
              </div>

              <NFormItem label="Assets">
                <NDynamicInput v-model:value="clientData.financialInfo.assets" :on-create="createAssetItem"
                  placeholder="Add asset">
                  <template #default="{ value, index }">
                    <div class="dynamic-input-row">
                      <NInput v-model:value="clientData.financialInfo.assets[index].type" placeholder="Asset type" />
                      <NInputNumber v-model:value="clientData.financialInfo.assets[index].value" placeholder="Value"
                        :min="0" :formatter="(value) => value ? `$${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''"
                        :parser="(value) => value ? value.replace(/\$\s?|(,*)/g, '') : ''" />
                      <NInput v-model:value="clientData.financialInfo.assets[index].description" placeholder="Description" />
                    </div>
                  </template>
                </NDynamicInput>
              </NFormItem>

              <NFormItem label="Liabilities">
                <NDynamicInput v-model:value="clientData.financialInfo.liabilities" :on-create="createLiabilityItem"
                  placeholder="Add liability">
                  <template #default="{ value, index }">
                    <div class="dynamic-input-row">
                      <NInput v-model:value="clientData.financialInfo.liabilities[index].type" placeholder="Liability type" />
                      <NInputNumber v-model:value="clientData.financialInfo.liabilities[index].amount" placeholder="Amount"
                        :min="0" :formatter="(value) => value ? `$${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''"
                        :parser="(value) => value ? value.replace(/\$\s?|(,*)/g, '') : ''" />
                      <NInput v-model:value="clientData.financialInfo.liabilities[index].description" placeholder="Description" />
                    </div>
                  </template>
                </NDynamicInput>
              </NFormItem>

              <NFormItem label="Bankruptcy History">
                <NSwitch v-model:value="clientData.financialInfo.bankruptcyHistory">
                  <template #checked>Yes</template>
                  <template #unchecked>No</template>
                </NSwitch>
                <span class="directive-note">I have filed for bankruptcy in the past</span>
              </NFormItem>
            </div>

            <NDivider />

            <!-- Emergency Contact -->
            <div class="form-section">
              <h3>Emergency Contact</h3>

              <div class="form-row">
                <NFormItem label="Emergency Contact Name">
                  <NInput v-model:value="clientData.emergencyContactName" placeholder="Contact person's name" />
                </NFormItem>

                <NFormItem label="Emergency Contact Phone">
                  <NInput v-model:value="clientData.emergencyContactPhone" placeholder="(555) 987-6543" />
                </NFormItem>
              </div>

              <NFormItem label="Relationship to Contact">
                <NInput v-model:value="clientData.emergencyContactRelationship"
                  placeholder="Spouse, Parent, Sibling, etc." />
              </NFormItem>
            </div>

            <NDivider />

            <!-- Special Requirements & Accessibility -->
            <div class="form-section">
              <h3>Special Requirements & Accessibility</h3>

              <NFormItem label="Accessibility Needs">
                <NSelect v-model:value="clientData.accessibilityNeeds" multiple :options="accessibilityNeedsOptions"
                  placeholder="Select any accessibility needs" clearable />
              </NFormItem>

              <NFormItem label="Special Requirements">
                <NInput v-model:value="clientData.specialRequirements" type="textarea"
                  placeholder="Any special requirements or accommodations needed"
                  :autosize="{ minRows: 2, maxRows: 4 }" />
              </NFormItem>

              <NFormItem label="Conflict of Interest">
                <NSwitch v-model:value="clientData.conflictOfInterest">
                  <template #checked>Yes</template>
                  <template #unchecked>No</template>
                </NSwitch>
                <span class="directive-note">I am aware of potential conflicts of interest</span>
              </NFormItem>

              <NFormItem label="Background Check Authorization">
                <NSwitch v-model:value="clientData.backgroundCheck">
                  <template #checked>Yes</template>
                  <template #unchecked>No</template>
                </NSwitch>
                <span class="directive-note">I authorize a background check if required</span>
              </NFormItem>
            </div>

            <NDivider />

            <!-- Additional Notes -->
            <div class="form-section">
              <h3>Additional Information</h3>

              <NFormItem label="Notes">
                <NInput v-model:value="clientData.notes" type="textarea"
                  placeholder="Additional notes about your legal needs" :autosize="{ minRows: 2, maxRows: 6 }" />
              </NFormItem>

              <NFormItem label="Consent" required>
                <NCheckbox v-model:checked="clientData.consentSigned">
                  I consent to the storage and processing of my legal information
                </NCheckbox>
              </NFormItem>
            </div>
          </NForm>

          <!-- Error/Success Messages -->
          <div v-if="errorMessage" class="message">
            <NAlert type="error" :title="errorMessage" closable />
          </div>

          <div v-if="successMessage" class="message">
            <NAlert type="success" :title="successMessage" />
          </div>

          <template #footer>
            <NSpace justify="space-between">
              <NButton @click="goToStep1" :disabled="isEditMode || isSaving" v-if="!isEditMode">
                <template #icon>
                  <NIcon>
                    <ArrowLeft />
                  </NIcon>
                </template>
                Back to Security
              </NButton>

              <div v-else></div>

              <NSpace>
                <NButton @click="cancelEdit" :disabled="isSaving">
                  Cancel
                </NButton>
                <NButton type="primary" @click="saveProfile" :loading="isSaving" :disabled="!isProfileDataValid">
                  <template #icon>
                    <NIcon>
                      <Save />
                    </NIcon>
                  </template>
                  {{ isEditMode ? 'Save Changes' : 'Create Profile' }}
                </NButton>
              </NSpace>
            </NSpace>
          </template>
        </NCard>
      </div>
    </div>
  </div>
</template>

<style scoped>
.profile-edit-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 20px;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 20px;
}

.loading-state p {
  margin-top: 16px;
  color: #666;
}

.page-header {
  text-align: center;
  margin-bottom: 32px;
}

.page-header h1 {
  margin: 0 0 8px 0;
  color: #333;
  font-size: 2.2rem;
  font-weight: 600;
}

.page-subtitle {
  margin: 0;
  color: #666;
  font-size: 1.1rem;
}

.steps-container {
  margin-bottom: 32px;
}

.step-card {
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.password-modal-content {
  text-align: center;
  padding: 16px 0;
}

.modal-icon {
  margin-bottom: 16px;
}

.security-notice {
  margin-bottom: 24px;
}

.form-section {
  margin-bottom: 24px;
}

.form-section h3 {
  margin: 0 0 16px 0;
  color: #333;
  font-size: 1.3rem;
  font-weight: 500;
}

.section-description {
  margin: 0 0 20px 0;
  color: #666;
  font-size: 0.95rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.dynamic-input-row {
  display: flex;
  gap: 10px;
  align-items: center;
  width: 100%;
}

.dynamic-input-row>* {
  flex: 1;
}

.directive-note {
  margin-left: 10px;
  font-size: 0.9rem;
  color: #666;
}

.password-error,
.message {
  margin: 16px 0;
}

.error-message {
  margin-top: 16px;
}

/* Responsive design */
@media (max-width: 768px) {
  .profile-edit-container {
    padding: 10px;
  }

  .page-header h1 {
    font-size: 1.8rem;
  }

  .form-row {
    grid-template-columns: 1fr;
    gap: 0;
  }

  .dynamic-input-row {
    flex-direction: column;
    gap: 8px;
  }

  .steps-container {
    margin-bottom: 20px;
  }
}

@media (max-width: 480px) {
  .page-header h1 {
    font-size: 1.6rem;
  }

  .page-subtitle {
    font-size: 1rem;
  }
}
</style>